import { parseRSS, RssItem } from '../../support/rssFeed';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useNavigate, useLocation } from 'react-router-dom';
import SecurityPlatformDiagram from './SecurityPlatformDiagram';
import {
  HbrButton,
  HbrIcon,
} from '@harbor/elements-react-standalone';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend, AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  navSites,
  navDocumentation,
  navSecurity, cloud, lock, checkCircle, navFirewall, globe,
  trendUp, navTools, terminalWindow, rocket,
  navMonitor, magicWand,
  navApplications, navOverview, navAdmin,
  navSuite,
  newChat,
} from '@harbor/elements/icons';

import { useSelector } from 'react-redux';
import { RootState } from '../../support/store';
import { buildApiUrl, getApiHeaders } from '../../support/queriesHelpers';
import { fetchItems } from '../../support/queries';
import { NAV_ITEMS } from '../../navigation/appNavConfig';
import { ADMIN_URL_USER_ACCESS } from '../Admin/PageStructure/admin-values';
import { useSfTeamOpportunities } from '../../hooks/useSfTeamOpportunities';
import type { SeActivitySummaryResponse } from '../../hooks/useSfTeamActivitySummary';
import { buildEnablementRows } from '../../support/enablementRows';
import { fmtMinutes } from '../../support/dateLabels';
import { useFiscalQuarters } from '../../hooks/useFiscalQuarters';
import { useEnablementItemMeta } from '../../hooks/enablement-item-meta-hooks';
import { demoEnablementRows, demoQuarterBounds } from '../../support/enablementRowsDemo';
import { useDemoFlag } from '../../support/useDemoFlag';
import { activityShare, average, bucketActivity, buildOpportunityStats, clampPercent, monthRanges, ownOpportunities, percentOf } from '../../support/profileMetrics';
import { useLeaderCourseCompletionsByUser, useTttBulkCertificateStatus } from '../../hooks/useMoodleLeaderWorkshops';
import { useMoodleCourses, useMoodleCategories } from '../../hooks/useMoodleHooks';
import { useJourneyMaps, useEnsureJourneyMapsLoaded } from '../../hooks/journey-map-hooks';
import { useEducationCompletionByEmail } from '../../hooks/education-completion-hooks';
import { useAttainmentByEmail } from '../../hooks/attainment-hooks';
import { useUserJourneyMapDetail } from '../../hooks/enablement-journey-map-hooks';
import { useSfTeamOpportunitiesPovLean } from '../../hooks/seSfTeamOpportunitiesPov';
import { cyberAcademyTrainingUrl, enablementPortalUrl } from '../../support/cyberAcademy';
import PortalChatbot from './PortalChatbot';
import SelfEvaluation from './SelfEvaluation';
import OpportunityActivity from './OpportunityActivity';
import EnablementTable from './EnablementTable';
import SystemBanners from './SystemBanners';
import './LandingPageV2.scss';
import '../Common/PortalShellHeader.scss';

/* ------------------------------------------------------------------ */
/*  Navigation items                                                   */
/* ------------------------------------------------------------------ */
// `permKey` maps to a permission check computed inside the component (see
// `portalApps` memo below) — tiles with no `permKey` are always shown
// (mirrors PortalShellHeader.tsx's toolbar access model: some tiles are
// gated on the same `page.*.view` permissions as the toolbar links, so a
// user who can't see e.g. Dashboard/Admin in the toolbar won't see the tile
// here either).
const PORTAL_APPS_BASE: Array<{ permKey?: string; title: string; path: string; icon: () => string; color: string; img?: string }> = [
  { permKey: 'leader', title: 'Leader', path: '/leader', icon: navSuite, color: '#0d7d7d' },
  { permKey: 'dashboard', title: 'Dashboard', path: '/dashboard', icon: navApplications, color: '#7c5bbf' },
  { permKey: 'opportunity', title: 'Opportunities', path: '/opportunity', icon: trendUp, color: '#3070e7' },
  { permKey: 'customer', title: 'Customers', path: '/customers', icon: navSites, color: '#16bdeb' },
  { permKey: 'campaign', title: 'Campaigns', path: '/campaigns', icon: rocket, color: '#ff9000' },
  { permKey: 'resources', title: 'Resources', path: '/solutions/architecture', icon: navOverview, color: '#e65100' },
  { permKey: 'admin', title: 'Admin', path: ADMIN_URL_USER_ACCESS, icon: navAdmin, color: '#5b6b79' },
  { title: 'Enablement', path: enablementPortalUrl(), icon: navDocumentation, color: '#6abf4b', img: '/assets/logos/cyberAcademyAltLogo.png' },
  { title: 'Demos', path: 'https://content.ciscodcloud.com/content/cisco-dcloud/us/en/home/new-demos.html', icon: terminalWindow, color: '#e04f7b' },
  { permKey: 'p2p', title: 'P2P', path: '/p2p', icon: newChat, color: '#3070e7' },
];

const findNavItem = (key: string) => NAV_ITEMS.find((item) => item.key === key);

/* ------------------------------------------------------------------ */
/*  Course gradients + SVGs                                            */
/* ------------------------------------------------------------------ */
const COURSE_CARDS = [
  { grad: 'linear-gradient(135deg, #3070e7, #16bdeb)', color: '#3070e7', icon: navSecurity },
  { grad: 'linear-gradient(135deg, #ff007f, #ff9000)', color: '#ff007f', icon: cloud },
  { grad: 'linear-gradient(135deg, #16bdeb, #3070e7)', color: '#16bdeb', icon: lock },
  { grad: 'linear-gradient(135deg, #ff9000, #ff007f)', color: '#ff9000', icon: checkCircle },
  { grad: 'linear-gradient(135deg, #3070e7, #16bdeb)', color: '#3070e7', icon: navFirewall },
  { grad: 'linear-gradient(135deg, #ff007f, #ff9000)', color: '#ff007f', icon: globe },
];

/* ------------------------------------------------------------------ */
/*  Mock data                                                          */
/* ------------------------------------------------------------------ */
const MOCK_USER = { name: 'Alex Johnson', email: 'ajohnson@cisco.com' };

/* Radar tooltip: each axis is a 0–100 percentage; the tooltip adds the underlying counts and shows the
   unclamped value (attainment can exceed 100%). */
const PerfTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e5ea', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#1a2332' }}>{row.subject}</div>
      <div style={{ color: '#049fd9' }}>You: {row.selfRaw}</div>
      {row.hasTeam && <div style={{ color: '#6b7280' }}>Team avg: {row.teamRaw}</div>}
    </div>
  );
};

const EVENT_COLORS: Record<string, string> = {
  Conference:  '#9b59b6',
  Hybrid:      '#ff9000',
  Virtual:     '#3070e7',
  'In-Person': '#e04f7b',
  Workshop:    '#16bdeb',
  Webinar:     '#6abf4b',
};

interface CalItem {
  title: string;
  day: number;
  month: number;
  type: string;
  required: 'mandatory' | 'optional';
  link?: string;
  location?: string;
  description?: string;
}

const REGION_FROM_TZ = (): 'amer' | 'emea' | 'apjc' | null => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (tz.startsWith('America/')) return 'amer';
    if (tz.startsWith('Europe/') || tz.startsWith('Africa/')) return 'emea';
    if (/^(Asia|Australia|Pacific)\//.test(tz)) return 'apjc';
  } catch (_) { /* timezone unavailable */ }
  return null;
};

const AMER_RE  = /usa|canada|mexico|brazil|argentina|colombia|chile|peru|scottsdale|las vegas|oak brook|winnipeg|detroit|san antonio/;
const EMEA_RE  = /uk|united kingdom|ireland|france|germany|spain|italy|switzerland|netherlands|sweden|norway|denmark|poland|portugal|austria|greece|turkey|uae|saudi|qatar|israel|south africa|egypt|cairo|madrid|paris|frankfurt|zurich|dublin|london/;
const APJC_RE  = /australia|new zealand|singapore|japan|korea|india|china|hong kong|taiwan|thailand|indonesia|malaysia|vietnam|philippines|melbourne|canberra/;

const eventRegion = (loc?: string): 'amer' | 'emea' | 'apjc' | null => {
  if (!loc) return null;
  const s = loc.toLowerCase();
  if (s.includes('north america') || s.includes('americas')) return 'amer';
  if (s.includes('emea') || s.includes('europe')) return 'emea';
  if (s.includes('apjc') || s.includes('apj') || s.includes('apac') || s.includes('asia pacific')) return 'apjc';
  if (AMER_RE.test(s)) return 'amer';
  if (EMEA_RE.test(s)) return 'emea';
  if (APJC_RE.test(s)) return 'apjc';
  return null;
};

const mapEventType = (raw: any, title: string): string => {
  const s = String(raw || '').toLowerCase();
  const t = title.toLowerCase();
  if (s.includes('virtual') || s.includes('webinar') || t.includes('webinar')) return 'Virtual';
  if (s.includes('workshop') || t.includes('workshop') || t.includes('test drive')) return 'Workshop';
  if (s.includes('hybrid') || t.includes('hybrid')) return 'Hybrid';
  if (s.includes('in-person') || s.includes('in person')) return 'In-Person';
  return 'Conference';
};

const mapEventRequired = (raw: any): 'mandatory' | 'optional' => {
  if (raw === true) return 'mandatory';
  const s = String(raw || '').trim().toLowerCase();
  if (['mandatory', 'required', 'true', 'yes', '1'].includes(s)) return 'mandatory';
  return 'optional';
};

const CISCO_EVENTS_STATIC: CalItem[] = [
  { title: 'Road to SASEmania',                          day: 30, month: 3,  type: 'Hybrid',     required: 'optional', location: 'Oak Brook, USA',       link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco and Optiv Security Workshop',          day: 12, month: 4,  type: 'In-Person',  required: 'optional', location: 'Winnipeg, Canada',     link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Supercharge AI with Cisco and Splunk',       day: 13, month: 4,  type: 'In-Person',  required: 'optional', location: 'Singapore',            link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Connect 2026 UK & Ireland',            day: 18, month: 4,  type: 'In-Person',  required: 'optional', location: 'London, United Kingdom', link: 'https://cvent.me/oQ0a5N?RefId=CCAL' },
  { title: 'Phoenix Cisco Users Group',                  day: 19, month: 4,  type: 'Hybrid',     required: 'optional', location: 'Scottsdale, USA',      link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'AI in Practice Test Drive — Egypt',          day: 20, month: 4,  type: 'Workshop',   required: 'optional', location: 'Cairo, Egypt',         link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Splunk Go Madrid',                           day: 21, month: 4,  type: 'In-Person',  required: 'optional', location: 'Madrid, Spain',        link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Connect 2026 France',                  day: 21, month: 4,  type: 'In-Person',  required: 'optional', location: 'Paris, France',        link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'AI in Practice Virtual Test Drive',          day: 28, month: 4,  type: 'Virtual',    required: 'optional', location: 'Virtual (UK)',         link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Live 2026 Las Vegas',                  day: 31, month: 4,  type: 'Hybrid',     required: 'optional', location: 'Las Vegas, NV',        link: 'https://www.ciscolive.com/global.html' },
  { title: 'Splunk Go London',                           day:  4, month: 5,  type: 'In-Person',  required: 'optional', location: 'London, UK',           link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Government Edge 2026',                       day: 10, month: 5,  type: 'In-Person',  required: 'optional', location: 'Canberra, Australia',  link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'ITS America',                                day: 10, month: 5,  type: 'Conference', required: 'optional', location: 'Detroit, USA',         link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'HITEC 2026',                                 day: 15, month: 5,  type: 'Conference', required: 'optional', location: 'San Antonio, USA',     link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Connect 2026 Germany',                 day: 16, month: 5,  type: 'In-Person',  required: 'optional', location: 'Frankfurt, Germany',   link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Future of Cyber Security',                   day: 16, month: 5,  type: 'Virtual',    required: 'optional', location: 'Virtual (UK)',         link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Connect 2026 Switzerland',             day: 17, month: 5,  type: 'In-Person',  required: 'optional', location: 'Zurich, Switzerland',  link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Splunk Go Dublin',                           day: 18, month: 5,  type: 'In-Person',  required: 'optional', location: 'Dublin, Ireland',      link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco at Ai4 2026',                          day:  4, month: 7,  type: 'In-Person',  required: 'optional', location: 'Las Vegas, NV',        link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
  { title: 'Cisco Live 2026 Melbourne',                  day:  9, month: 10, type: 'Hybrid',     required: 'optional', location: 'Melbourne, Australia', link: 'https://www.cisco.com/site/us/en/learn/events/index.html' },
];

const CAL_YEAR = new Date().getFullYear();
const CAL_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MOCK_CERTS = [
  { name: 'Cisco CyberOps Associate', status: 'Active', expiry: 'Dec 2026' },
  { name: 'CCNP Security', status: 'Active', expiry: 'Jun 2027' },
  { name: 'Cisco XDR Specialist', status: 'Active', expiry: 'Sep 2026' },
  { name: 'AWS Security Specialty', status: 'Expiring', expiry: 'May 2026' },
  { name: 'Cisco AI Defense Associate', status: 'Expired', expiry: 'Jan 2026' },
];



const MOCK_COURSES = [
  { id: 901, fullname: 'Cisco XDR Fundamentals', summary: 'Learn the fundamentals of Cisco Extended Detection and Response platform.', categoryid: 1, duration: '4h 30m', level: 'Beginner' },
  { id: 902, fullname: 'SASE/SSE Deep Dive', summary: 'Secure Access Service Edge architecture and deployment strategies.', categoryid: 1, duration: '6h 15m', level: 'Advanced' },
  { id: 903, fullname: 'Cloud Security Architecture', summary: 'Designing secure cloud-native architectures with Cisco solutions.', categoryid: 2, duration: '5h', level: 'Intermediate' },
  { id: 904, fullname: 'Zero Trust Workshop', summary: 'Hands-on workshop for implementing zero trust security models.', categoryid: 2, duration: '3h 45m', level: 'Intermediate' },
  { id: 905, fullname: 'Firewall Migration Cert', summary: 'Step-by-step firewall migration from legacy to next-gen platforms.', categoryid: 1, duration: '8h', level: 'Advanced' },
  { id: 906, fullname: 'Endpoint Security Mastery', summary: 'Advanced endpoint protection techniques and best practices.', categoryid: 3, duration: '5h 30m', level: 'Advanced' },
];
const MOCK_CATEGORIES = [
  { id: 1, name: 'Security' },
  { id: 2, name: 'Architecture' },
  { id: 3, name: 'Endpoint' },
];

const MOCK_RECOMMENDED = [
  { title: 'Cisco AI Defense Fundamentals', reason: 'Based on your role — AI security is a top priority this quarter', duration: '3h 15m', level: 'Beginner', color: '#3070e7' },
  { title: 'Advanced XDR Threat Hunting', reason: 'You completed XDR Fundamentals — take the next step', duration: '5h', level: 'Advanced', color: '#e04f7b' },
  { title: 'Cisco Cyber Vision for OT/IoT', reason: 'Expanding OT/IoT security coverage across your accounts', duration: '4h', level: 'Intermediate', color: '#e65100' },
  { title: 'Zero Trust Architecture for SEs', reason: 'Top-rated course among SEs in your region', duration: '6h 30m', level: 'Intermediate', color: '#16bdeb' },
  { title: 'Hybrid Mesh Firewall Deployment', reason: 'Trending — 85% of SEs completed this in Q1', duration: '4h 45m', level: 'Advanced', color: '#6abf4b' },
  { title: 'Customer Demo Mastery', reason: 'Improve your demo skills — linked to higher win rates', duration: '2h', level: 'Beginner', color: '#7c5bbf' },
];

const MOCK_GSSO_TRAINING = [
  { name: 'Security SE Foundations FY26', status: 'Completed', due: 'Mar 2026' },
  { name: 'Cisco XDR Technical Readiness', status: 'Completed', due: 'Feb 2026' },
  { name: 'AI Defense Sales Enablement', status: 'In Progress', due: 'May 2026' },
  { name: 'FY26 Q3 Security Portfolio Update', status: 'Not Started', due: 'Jun 2026' },
  { name: 'Compliance & Ethics Annual', status: 'Completed', due: 'Jan 2026' },
];

const MOCK_CORPORATE_TRAINING = [
  { name: 'Partner Co-Sell Workshop: SASE/SSE', status: 'Completed', due: 'Feb 2026' },
  { name: 'Corporate Demo Lab: Firewall Migration', status: 'In Progress', due: 'Apr 2026' },
  { name: 'Joint Customer Success Playbook', status: 'Not Started', due: 'May 2026' },
  { name: 'Partner Technical Deep-Dive: Hypershield', status: 'Not Started', due: 'Jun 2026' },
];

/* ------------------------------------------------------------------ */
/*  RSS                                                                */
/* ------------------------------------------------------------------ */

const MOCK_RSS: RssItem[] = [
  { title: 'Cisco XDR: Unifying Detection & Response Across the Stack', description: 'Explore how Cisco XDR correlates telemetry across endpoints, network, cloud, and email.', link: '#', pubDate: 'Mar 28, 2026' },
  { title: 'SASE/SSE Adoption — What SEs Need to Know for FY26', description: 'Key talking points and competitive positioning for SASE conversations.', link: '#', pubDate: 'Mar 25, 2026' },
  { title: 'Talos Threat Spotlight: Ransomware Trends in Q1', description: 'The latest intelligence from Cisco Talos on evolving ransomware tactics.', link: '#', pubDate: 'Mar 20, 2026' },
  { title: 'Zero Trust Workshop — New Lab Content Available', description: 'Hands-on lab environments for the updated Zero Trust workshop are now live.', link: '#', pubDate: 'Mar 15, 2026' },
];

const TALOS_BLOG = 'https://blog.talosintelligence.com/';
/** A post's own URL, or the Talos blog when the item has none (the placeholder items). */
const postUrl = (link: string) => (/^https?:\/\//i.test(link) ? link : TALOS_BLOG);

const fmtDate = (d: string) =>
  d ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(d)) : '';

/* ================================================================== */
/*  COMPONENT                                                          */
/* ================================================================== */
const LandingPageV2 = () => {
  const navigate = useNavigate();
  const { name: userName, email: userEmail, isManager, id: userDbId } = useSelector((state: RootState) => state.user.userData);

  /* Access control for portal tiles — mirrors PortalShellHeader.tsx's toolbar
     permission model so a tile is only shown here if the user would also see
     the equivalent link in the toolbar. */
  const authz = useSelector((state: RootState) => state.user?.authz);
  const permSet = useMemo(() => new Set<string>(authz?.uiPermissions || []), [authz?.uiPermissions]);
  const isAdminFull = permSet.has('admin.full');
  const canAccessPage = (pageViewPerm?: string, opts?: { beta?: boolean }) => {
    if (isAdminFull) return true;
    if (!pageViewPerm) return false;
    if (!permSet.has(pageViewPerm)) return false;
    if (opts?.beta && !permSet.has('page.beta')) return false;
    return true;
  };

  const dashboardNavItem = findNavItem('dashboard');
  const customerNavItem = findNavItem('customer');
  const opportunityNavItem = findNavItem('opportunity');
  const workshopsNavItem = findNavItem('workshops');
  const solutionsNavItem = findNavItem('solutions');
  const msspNavItem = findNavItem('mssp');
  const approvalsNavItem = findNavItem('approvals');
  const adminNavItem = findNavItem('admin');
  const p2pNavItem = findNavItem('p2p');
  const campaignNavItem = findNavItem('campaign');
  const leaderNavItem = findNavItem('leader');

  const canSeeDashboard = canAccessPage(dashboardNavItem?.pageView, { beta: dashboardNavItem?.beta });
  const canSeeCustomers = canAccessPage(customerNavItem?.pageView, { beta: customerNavItem?.beta });
  const canSeeOpportunities = canAccessPage(opportunityNavItem?.pageView, { beta: opportunityNavItem?.beta });
  const canSeeWorkshops = canAccessPage(workshopsNavItem?.pageView, { beta: workshopsNavItem?.beta });
  const canSeeSolutions = canAccessPage(solutionsNavItem?.pageView, { beta: solutionsNavItem?.beta });
  const canSeeMssp = canAccessPage(msspNavItem?.pageView, { beta: msspNavItem?.beta });
  const canSeeApprovals = canAccessPage(approvalsNavItem?.pageView, { beta: approvalsNavItem?.beta });
  const canSeeAdmin = canAccessPage(adminNavItem?.pageView, { beta: adminNavItem?.beta });
  const canSeeP2p = canAccessPage(p2pNavItem?.pageView, { beta: p2pNavItem?.beta });
  const canSeeCampaign = canAccessPage(campaignNavItem?.pageView, { beta: campaignNavItem?.beta });
  // The Leader tile is manager-only, and still respects the leader page permission.
  const canSeeLeader = isManager && canAccessPage(leaderNavItem?.pageView, { beta: leaderNavItem?.beta });
  const canSeeResources = canSeeWorkshops || canSeeSolutions || canSeeMssp || canSeeApprovals;

  const portalApps = useMemo(() => {
    const visibility: Record<string, boolean> = {
      leader: canSeeLeader,
      dashboard: canSeeDashboard,
      opportunity: canSeeOpportunities,
      customer: canSeeCustomers,
      campaign: canSeeCampaign,
      resources: canSeeResources,
      admin: canSeeAdmin,
      p2p: canSeeP2p,
    };
    return PORTAL_APPS_BASE.filter((app) => !app.permKey || visibility[app.permKey]);
  }, [canSeeLeader, canSeeDashboard, canSeeOpportunities, canSeeCustomers, canSeeCampaign, canSeeResources, canSeeAdmin, canSeeP2p]);

  /* Journey maps */
  useEnsureJourneyMapsLoaded();
  const journeyMaps = useJourneyMaps();

  /* Cisco Events — fetch from backend proxy endpoint */
  const userRegion = useMemo(REGION_FROM_TZ, []);
  const { data: publicCalItems = CISCO_EVENTS_STATIC } = useQuery<CalItem[]>({
    queryKey: ['cisco-events', userRegion],
    queryFn: async () => {
      let jsonItems: CalItem[] = [];
      try {
        const resp = await fetch(
          buildApiUrl('news/events/'),
          {
            headers: getApiHeaders(null, 'GET', 'news/events/'),
            credentials: 'include',
          }
        );
        if (resp.ok) {
          const json = await resp.json();
          // data.data contains the parsed JSON from the events endpoint
          const raw: any[] = Array.isArray(json?.data?.events) ? json.data.events : Array.isArray(json?.data) ? json.data : [];
          if (raw.length > 0) {
            jsonItems = raw
              .filter((e) => {
                if (!userRegion) return true;
                const explicit = String(e?.region || e?.eventRegion || '').toLowerCase();
                const r = (['amer', 'emea', 'apjc'] as const).find((x) => x === explicit) ?? eventRegion(String(e?.location || e?.eventLocation || e?.geo || ''));
                return !r || r === userRegion;
              })
              .map((e): CalItem | null => {
                const title = String(e?.eventTitle || e?.title || e?.displayTitle || e?.name || '').trim();
                const rawDate = e?.startDate || e?.date || e?.eventDate || e?.displayDate || e?.start_date || '';
                const d = new Date(rawDate);
                // calendar is current-year only
                if (!title || Number.isNaN(d.getTime()) || d.getFullYear() !== CAL_YEAR) return null;
                return {
                  title,
                  day: d.getDate(),
                  month: d.getMonth(),
                  type: mapEventType(e?.eventType || e?.type || e?.format, title),
                  required: mapEventRequired(e?.required ?? e?.eventRequired ?? e?.mandatory),
                  link: e?.eventUrl || e?.url || e?.link || undefined,
                  location: e?.eventLocation || e?.location || e?.eventRegion || undefined,
                  description: e?.description || e?.eventDescription || undefined,
                };
              })
              .filter((e): e is CalItem => !!e)
              .sort((a, b) => a.month - b.month || a.day - b.day);
          }
        }
      } catch (_) { /* fetch failed, fall back to static */ }
      const merged = [...CISCO_EVENTS_STATIC, ...jsonItems];
      const deduped = merged.filter((evt, idx, arr) =>
        arr.findIndex((x) => x.day === evt.day && x.month === evt.month && x.title.trim().toLowerCase() === evt.title.trim().toLowerCase()) === idx
      );
      return deduped.sort((a, b) => a.month - b.month || a.day - b.day);
    },
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  /* Admin-added events (toolbox_db) */
  const { data: adminCalItems = [] } = useQuery<CalItem[]>({
    queryKey: ['admin-calendar-events', userRegion],
    queryFn: async () => {
      const rows: any[] = await fetchItems('db/calendar-event', { active: true });
      if (!Array.isArray(rows)) return [];
      return rows
        .filter((e) => !userRegion || !e?.region || e.region === 'global' || e.region === userRegion)
        .map((e): CalItem | null => {
          // split the parts so a timezone offset can't shift the day
          const [y, m, d] = String(e?.start_date || '').split('-').map(Number);
          if (!e?.title || !y || !m || !d || y !== CAL_YEAR) return null;
          return {
            title: String(e.title),
            day: d,
            month: m - 1,
            type: mapEventType(e.event_type, String(e.title)),
            required: e.required ? 'mandatory' : 'optional',
            link: e.link || undefined,
            location: e.location || undefined,
            description: e.description || undefined,
          };
        })
        .filter((e): e is CalItem => !!e);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const allCalItems = useMemo(() => {
    const merged = [...publicCalItems, ...adminCalItems];
    const seen = new Set<string>();
    return merged
      .filter((evt) => {
        const key = `${evt.month}-${evt.day}-${evt.title.trim().toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.month - b.month || a.day - b.day);
  }, [publicCalItems, adminCalItems]);

  const [view, setView] = useState<'home' | 'profile'>('home');
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setView(params.get('view') === 'profile' ? 'profile' : 'home');
  }, [location.search]);
  const [calMonth, setCalMonth] = useState(() => new Date().getMonth());
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  /* Moodle training completions + certificates */
  const { data: moodleCompletions } = useLeaderCourseCompletionsByUser(userEmail);
  const { data: certStatus } = useTttBulkCertificateStatus(userEmail ? [userEmail] : null);

  /* Moodle */
  const { data: moodleCourses } = useMoodleCourses();
  const { data: moodleCategories } = useMoodleCategories();
  const courses = useMemo(() => (moodleCourses?.length ? moodleCourses : MOCK_COURSES), [moodleCourses]);
  const categories: Array<{ id: number; name: string }> = useMemo(
    () => (moodleCategories?.length ? moodleCategories : MOCK_CATEGORIES),
    [moodleCategories]
  );

  /* ---- Performance Pulse (self vs team) + "Stay on Track" data sources ---- */
  // Mindtickle education completion (self + direct-report summaries) and CENTRO attainment, addressed by
  // email. Both 400 in local/demo mode and 403 out of org scope — the memos below degrade to self-only.
  const { data: eduDrill, isLoading: eduLoading } = useEducationCompletionByEmail(userEmail || undefined);
  const { data: attainDrill } = useAttainmentByEmail(userEmail || undefined);
  // Journey-map detail for the current user (needs the toolbox DB user id), used by Stay on Track.
  const myJourney = useUserJourneyMapDetail(userDbId);
  const { data: povSummary } = useSfTeamOpportunitiesPovLean(userEmail, { enabled: !!userEmail, view: 'summary' }) as { data: any };

  /* Enablement tables: journey-map assignments + Mindtickle series, one row model */
  const demoEnablement = useDemoFlag('enablement');
  const { bounds: liveBounds } = useFiscalQuarters();
  const quarterBoundsForTable = useMemo(() => (demoEnablement ? demoQuarterBounds() : liveBounds), [demoEnablement, liveBounds]);
  const { data: itemMeta } = useEnablementItemMeta();
  const adminDueDates = useMemo(() => {
    const out: Record<string, string> = {};
    for (const m of itemMeta ?? []) if (m.due_date) out[`${m.source}:${m.source_id}`] = m.due_date;
    return out;
  }, [itemMeta]);
  const enablementRows = useMemo(
    () =>
      demoEnablement
        ? demoEnablementRows()
        : buildEnablementRows(myJourney.maps || [], eduDrill?.node?.own?.items || [], {
            catalog: journeyMaps,
            bounds: liveBounds,
            adminDueDates,
          }),
    [demoEnablement, myJourney.maps, eduDrill, journeyMaps, liveBounds, adminDueDates],
  );
  const enablementLoading = !demoEnablement && (myJourney.isLoading || eduLoading);

  /* Derived profile data from SF opps */
  const { data: teamOppsData, isSuccess: oppsLoaded } = useSfTeamOpportunities(userEmail, { enabled: !!userEmail });
  const oppStats = useMemo(
    () => buildOpportunityStats(ownOpportunities(teamOppsData, userEmail), oppsLoaded),
    [teamOppsData, userEmail, oppsLoaded],
  );

  /* Monthly engagement: one se-activity-summary call per calendar month (own counts), plus a 12-month total for the radar */
  const monthWindows = useMemo(() => monthRanges(new Date(), 12), []);
  const normalizedEmail = (userEmail || '').toLowerCase();
  const monthlyQueries = useQueries({
    queries: monthWindows.map((m) => ({
      queryKey: ['SfTeamActivitySummary', normalizedEmail, m.filter],
      queryFn: (): Promise<SeActivitySummaryResponse> =>
        fetchItems('sf/team/se-activity-summary/', { email: normalizedEmail, current_time_filter: m.filter }, { includePageSize: false }),
      enabled: !!normalizedEmail,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    })),
  });
  const engagementData = useMemo(
    () => monthWindows.map((m, i) => bucketActivity(m.label, (monthlyQueries[i]?.data as SeActivitySummaryResponse | undefined)?.tree?.own)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthWindows, ...monthlyQueries.map((q) => q.data)],
  );
  /* Derived training data from Moodle completions, certs, and courses */
  const trainingData = useMemo(() => {
    const completionRows: any[] = Array.isArray(moodleCompletions?.results) ? moodleCompletions.results : Array.isArray(moodleCompletions) ? moodleCompletions : [];
    const userCerts: any[] = certStatus && userEmail ? (certStatus[userEmail.toLowerCase()] || []) : [];
    const courseList: any[] = Array.isArray(courses) ? courses : [];
    const catMap = new Map((Array.isArray(categories) ? categories : []).map((c: any) => [c.id, c.name]));

    // Certs: from certificate status endpoint
    const certs = userCerts.length
      ? userCerts.map((c: any) => ({
          name: c.course_fullname || 'Certificate',
          status: c.has_certificate ? 'Active' : 'Expiring',
          expiry: c.category_name || 'N/A',
        }))
      : completionRows.filter((c: any) => c.completion_status === 'complete').map((c: any) => ({
          name: c.fullname || c.course_name || 'Course',
          status: 'Active',
          expiry: c.date_completed_utc
            ? new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(new Date(c.date_completed_utc))
            : 'N/A',
        }));

    // Split training by Moodle category
    const completedNames = new Set(completionRows.filter((c: any) => c.completion_status === 'complete').map((c: any) => (c.fullname || c.course_name || '').toLowerCase()));
    const mapRow = (c: any) => ({
      name: c.fullname || c.course_name || 'Course',
      status: c.completion_status === 'complete' ? 'Completed' : c.completion_status === 'inprogress' ? 'In Progress' : 'Not Started',
      due: c.date_completed_utc || 'TBD',
    });

    // Category-based split: "Active Workshops" → workshops, "Train the Trainer" → ttt, rest → general
    const workshopRows = completionRows.filter((c: any) => (c.category || '').toLowerCase().includes('active workshops'));
    const tttRows = completionRows.filter((c: any) => (c.category || '').toLowerCase().includes('train the trainer'));
    const otherRows = completionRows.filter((c: any) => {
      const cat = (c.category || '').toLowerCase();
      return !cat.includes('active workshops') && !cat.includes('train the trainer');
    });

    let gsso: any[];
    let corporate: any[];
    if (completionRows.length) {
      // Use category-based split if we have data
      gsso = (otherRows.length ? otherRows : completionRows).slice(0, 5).map(mapRow);
      corporate = [...workshopRows, ...tttRows].slice(0, 5).map(mapRow);
    } else if (courseList.length) {
      // No completions — split courses by category
      const workshopCatIds = new Set((Array.isArray(categories) ? categories : [])
        .filter((c: any) => (c.name || '').toLowerCase().includes('workshop') || (c.name || '').toLowerCase().includes('train'))
        .map((c: any) => c.id));
      gsso = courseList.filter((c: any) => !workshopCatIds.has(c.categoryid)).slice(0, 5).map((c: any) => ({
        name: c.fullname || 'Course',
        status: 'Not Started',
        due: 'Available',
      }));
      corporate = courseList.filter((c: any) => workshopCatIds.has(c.categoryid)).slice(0, 5).map((c: any) => ({
        name: c.fullname || 'Course',
        status: 'Not Started',
        due: 'Available',
      }));
    } else {
      gsso = [];
      corporate = [];
    }

    // Recommended: courses user hasn't completed
    const RECO_COLORS = ['#3070e7', '#e04f7b', '#e65100', '#16bdeb', '#6abf4b', '#7c5bbf'];
    const recommended = courseList
      .filter((c: any) => !completedNames.has((c.fullname || '').toLowerCase()))
      .slice(0, 6)
      .map((c: any, i: number) => ({
        title: c.fullname || 'Course',
        reason: `Category: ${catMap.get(c.categoryid) || 'General'}`,
        duration: c.duration || '',
        level: c.level || '',
        color: RECO_COLORS[i % RECO_COLORS.length],
      }));

    return {
      certs: certs.length ? certs.slice(0, 5) : MOCK_CERTS,
      gsso: gsso.length ? gsso : MOCK_GSSO_TRAINING,
      corporate: corporate.length ? corporate : MOCK_CORPORATE_TRAINING,
      recommended: recommended.length ? recommended : MOCK_RECOMMENDED,
    };
  }, [moodleCompletions, certStatus, userEmail, courses, categories]);

  /* ---- Performance Pulse: five 0–100 axes, you vs direct-team average ----
     Every axis is a percentage on its own absolute scale, so the team average is a reference point and never
     the 100% mark. `null` means no data for that side (e.g. an IC has no team). */
  const pulseAxes = useMemo(() => {
    // Required Training: Mindtickle required series completed / required
    const eduNode = eduDrill?.node ?? null;
    const reqPct = (s: any) => percentOf(s?.required_completed, s?.required_total);
    const selfSummary = eduNode?.own?.summary;
    const selfReq = reqPct(selfSummary);
    const teamReq = eduNode?.reports?.length
      ? average((eduNode.reports.map((r: any) => reqPct(r.own?.summary)).filter((v: number | null): v is number => v != null)))
      : null;

    // Attainment: attainment_pct is a 0–1 fraction; can exceed 100% so the plot is clamped, the tooltip is not
    const attNode = attainDrill?.node ?? null;
    const selfAtt = attNode?.own?.attainment_pct != null ? attNode.own.attainment_pct * 100 : null;
    const teamAtt = attNode?.reports?.length
      ? average(attNode.reports.map((r: any) => r.own?.attainment_pct).filter((v: number | null): v is number => v != null).map((v: number) => v * 100))
      : null;

    // POV Coverage: opportunities with POV activity / opportunities
    const povCoverage = (c: any) => percentOf(c?.opp_with_pov_count, c?.opp_count);
    const povSelf = povSummary?.self?.own ?? povSummary?.self?.rollup;
    const selfPov = povCoverage(povSelf);
    const teamPov = average(
      Object.values<any>(povSummary?.reports ?? {})
        .map((r) => (r?.own?.opp_count ? povCoverage(r.own) : null))
        .filter((v): v is number => v != null),
    );

    // Demo and Customer Design: the share of your SE activity (last 12 months) that is each kind
    const trees = monthlyQueries.map((q) => (q.data as SeActivitySummaryResponse | undefined)?.tree);
    const demoShare = activityShare(trees, 'demoPitch');
    const designShare = activityShare(trees, 'design');
    const shareText = (s: ReturnType<typeof activityShare>) =>
      s.self != null ? `${Math.round(s.self)}% (${s.selfCount} of ${s.selfTotal} activities)` : '—';

    const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v)}%`);
    return [
      { key: 'training', label: 'Required Training', self: selfReq, team: teamReq,
        selfText: selfSummary?.required_total ? `${selfReq != null ? Math.round(selfReq) : 0}% (${selfSummary.required_completed} of ${selfSummary.required_total} required)` : '—', teamText: pct(teamReq) },
      { key: 'attainment', label: 'Attainment', self: selfAtt, team: teamAtt, selfText: pct(selfAtt), teamText: pct(teamAtt) },
      { key: 'pov', label: 'POV Coverage', self: selfPov, team: teamPov,
        selfText: povSelf?.opp_count ? `${pct(selfPov)} (${povSelf.opp_with_pov_count} of ${povSelf.opp_count} opps)` : '—', teamText: pct(teamPov) },
      { key: 'demo', label: 'Demo', self: demoShare.self, team: demoShare.team,
        selfText: shareText(demoShare), teamText: pct(demoShare.team) },
      { key: 'design', label: 'Customer Design', self: designShare.self, team: designShare.team,
        selfText: shareText(designShare), teamText: pct(designShare.team) },
    ];
  }, [eduDrill, attainDrill, povSummary, monthlyQueries]);

  const hasTeamData = useMemo(() => pulseAxes.some((m) => m.team != null), [pulseAxes]);

  const radarData = useMemo(
    () =>
      pulseAxes.map((m) => ({
        subject: m.label,
        selfNorm: clampPercent(m.self),
        teamNorm: clampPercent(m.team),
        selfRaw: m.selfText,
        teamRaw: m.teamText,
        hasTeam: hasTeamData,
      })),
    [pulseAxes, hasTeamData],
  );

  /* ---- Stay on Track: newest incomplete journey maps + required-not-completed Mindtickle courses ---- */
  const stayOnTrack = useMemo(() => {
    // GSSO Required Training = newest incomplete journey maps (assignment id desc as a recency proxy,
    // since the assignment row exposes no created timestamp in the typed shape).
    const gsso = (myJourney.maps || [])
      .filter((m) => !m.done)
      .slice()
      .sort((a, b) => (b.assignmentId || 0) - (a.assignmentId || 0))
      .slice(0, 5)
      .map((m) => ({
        label: m.mapName,
        status: m.percent != null && m.percent > 0 ? 'In Progress' : 'Not Started',
        detail: m.percent != null ? `${Math.round(m.percent)}% complete` : 'Not started',
        // The journey-map id is the Cyber Academy training id; the origin is deployment-resolved.
        href: cyberAcademyTrainingUrl(m.mapId),
      }));

    // Required Corporate Training = admin-required Mindtickle series not yet completed.
    // Backend already drops closed/stale-FY series from `required`, but we re-apply the same
    // guards client-side so the list is correct even before the backend fix is deployed:
    //  - certified series are complete (optional modules never count against the learner);
    //  - series Mindtickle has closed/archived are no longer actionable;
    //  - series tagged with a fiscal year older than the current one are stale enablement.
    const now = new Date();
    const currentFY = (now.getFullYear() + (now.getMonth() >= 7 ? 1 : 0)) % 100;
    const isStaleFY = (name: string) => {
      const tokens = [...(name || '').matchAll(/FY\s*'?(\d{2})/gi)].map((m) => Number(m[1]));
      return tokens.length > 0 && Math.max(...tokens) < currentFY;
    };
    const items = eduDrill?.node?.own?.items || [];
    const corporate = items
      .filter(
        (i) =>
          i.required &&
          i.status !== 'completed' &&
          !i.certified &&
          !i.closed &&
          !isStaleFY(i.series_name),
      )
      .slice(0, 5)
      .map((i) => ({
        label: i.series_name,
        status: i.status === 'in_progress' ? 'In Progress' : 'Not Started',
        detail: `${Math.round(i.percent_completed || 0)}% complete`,
        href: i.series_id
          ? `https://cisco.mindtickle.com/new/ui/learner/training/programs/${i.series_id}`
          : null,
      }));

    return { gsso, corporate };
  }, [myJourney.maps, eduDrill]);

  /* RSS — fetch from backend endpoint that handles CORS */
  const [rssItems, setRssItems] = useState<RssItem[]>(MOCK_RSS);
  const [feedLoaded, setFeedLoaded] = useState(false);

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        // Fetch Talos feed from backend endpoint
        const resp = await fetch(
          buildApiUrl('news/rss/', {
            feed_url: 'https://blog.talosintelligence.com/feeds/posts/default',
          }),
          {
            headers: getApiHeaders(null, 'GET', 'news/rss/'),
            credentials: 'include',
          }
        );
        const data = await resp.json();
        // data.feed contains the RSS response
        if (!off && data.feed) {
          const parsed = parseRSS(data.feed);
          if (parsed.length) {
            setRssItems(parsed);
            setFeedLoaded(true);
          }
        }
      } catch { /* keep mock / show banner fallback */ }
    })();
    return () => { off = true; };
  }, []);

  /* Course slider */
  const sliderRef = useRef<HTMLDivElement>(null);
  const scroll = useCallback((dir: 'left' | 'right') => {
    const el = sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -el.offsetWidth * 0.6 : el.offsetWidth * 0.6, behavior: 'smooth' });
  }, []);

  const calMonthItems = useMemo(
    () => allCalItems.filter(e => e.month === calMonth),
    [allCalItems, calMonth]
  );

  const catById = useMemo(
    () => new Map(categories.map(c => [c.id, c.name])),
    [categories]
  );

  const today = new Date();

  return (
    <div className="v2">
      {/* The slim portal navbar was removed in favour of the shared full PortalShellHeader,
          which App.tsx now renders on /portal (see showShellHeader). */}

      {/* ===== HOME VIEW ===== */}
      {view === 'home' && (
        <>
          {/* Admin-managed system banners (db SystemBanner). The former hardcoded Frontier AI
              Workshop promo is now the first seeded row, edited via Admin → Toolbox Configuration
              → Banners; scheduling, severity and per-user dismiss are handled in the component. */}
          <SystemBanners />

          {/* Hero — commented out for the time being per request.
          <section className="v2__hero">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Welcome back, {(userName || MOCK_USER.name).split(' ')[0]}</p>
              <h1 className="v2__hero-heading">Your Security <span className="v2__hero-heading--accent">Hub</span></h1>
              <p className="v2__hero-sub">
                Everything you need to manage customers, pipeline, and enablement in one place.
              </p>
              <div className="v2__hero-actions">
                <HbrButton onClick={() => navigate('/dashboard')}>Go to Dashboard</HbrButton>
                <HbrButton variant="outline" onClick={() => setView('profile')}>View Profile</HbrButton>
              </div>
            </div>
          </section>
          */}

          {/* Security Portal — App Icons */}
          <section className="v2__section">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Security Portal</p>
              <h2 className="v2__heading">One Platform, Access Everything You Need</h2>
              <div className="v2__apps-row">
                {portalApps.map(app => (
                  <div
                    key={app.title}
                    className="v2__app"
                    onClick={() => {
                      if (/^https?:\/\//.test(app.path)) {
                        window.open(app.path, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate(app.path);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={`v2__app-icon ${app.img ? 'v2__app-icon--plain' : ''}`} style={app.img ? {} : { background: `${app.color}12`, color: app.color }}>
                      {app.img ? <img src={app.img} alt={app.title} className="v2__app-logo" /> : <HbrIcon asset={app.icon} />}
                    </div>
                    <span className="v2__app-name">{app.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Platform — Unified Security Architecture */}
          <section className="v2__section v2__section--platform" style={{ backgroundImage: 'url(/assets/images/cisco-hmf-hero.jpg)' }}>
            <div className="v2__wrap">
              <div className="v2__platform-layout">
                <div className="v2__platform-diagram-wrap">
                  <SecurityPlatformDiagram />
                </div>
                <div className="v2__unified">
                  <h2 className="v2__unified-title">
                    Unified Security<br />Architecture
                  </h2>
                  <div className="v2__unified-line" />
                  <p className="v2__unified-desc">
                    Explore our comprehensive security solutions designed to protect our organization with integrated threat protection, zero-trust access, and intelligent automation.
                  </p>
                  
                </div>
              </div>
            </div>
          </section>

          {/* Events – Calendar View */}
          <section className="v2__section v2__section--alt">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Upcoming Events</p>
              <h2 className="v2__heading">Events &amp; Webinars</h2>
              <p className="v2__subtext">Webinars, workshops, and labs to keep your skills sharp and your customers engaged.</p>

              <div className="v2__cal-layout">
                {/* Calendar grid */}
                <div className="v2__cal">
                  <div className="v2__cal-nav">
                    <button className="v2__cal-nav-btn" onClick={() => setCalMonth(m => Math.max(0, m - 1))} disabled={calMonth === 0}>&lsaquo;</button>
                    <span className="v2__cal-header">{MONTH_NAMES[calMonth]} {CAL_YEAR}</span>
                    <button className="v2__cal-nav-btn" onClick={() => setCalMonth(m => Math.min(11, m + 1))} disabled={calMonth === 11}>&rsaquo;</button>
                  </div>

                  <div className="v2__cal-weekdays">
                    {CAL_WEEKDAYS.map(d => <div key={d} className="v2__cal-wd">{d}</div>)}
                  </div>
                  <div className="v2__cal-grid">
                    {Array.from({ length: new Date(CAL_YEAR, calMonth, 1).getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} className="v2__cal-cell v2__cal-cell--empty" />
                    ))}
                    {Array.from({ length: new Date(CAL_YEAR, calMonth + 1, 0).getDate() }, (_, i) => i + 1).map(day => {
                      const evts = calMonthItems.filter(e => e.day === day);
                      const isToday = today.getFullYear() === CAL_YEAR && today.getMonth() === calMonth && today.getDate() === day;
                      return (
                        <div
                          key={day}
                          className={`v2__cal-cell ${evts.length ? 'v2__cal-cell--has-event' : ''} ${isToday ? 'v2__cal-cell--today' : ''} ${hoveredDay === day ? 'v2__cal-cell--hovered' : ''}`}
                          onMouseEnter={() => evts.length > 0 && setHoveredDay(day)}
                          onMouseLeave={() => setHoveredDay(null)}
                        >
                          <span className="v2__cal-day">{day}</span>
                          {evts.length > 0 && (
                            <div className="v2__cal-dots">
                              {evts.slice(0, 3).map((e, j) => (
                                <span key={j} className="v2__cal-dot" style={{ background: EVENT_COLORS[e.type] || '#3070e7' }} title={e.title} />
                              ))}
                              {evts.length > 3 && <span className="v2__cal-dot-more">+{evts.length - 3}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Legend */}
                  <div className="v2__cal-legend">
                    {Object.entries(EVENT_COLORS).map(([type, color]) => (
                      <div key={type} className="v2__cal-legend-item">
                        <span className="v2__cal-legend-dot" style={{ background: color }} />
                        <span>{type}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Event list sidebar */}
                <div className="v2__cal-list">
                  <div className="v2__cal-list-header">
                    {hoveredDay ? `${MONTH_NAMES[calMonth]} ${hoveredDay}` : `${MONTH_NAMES[calMonth]} ${CAL_YEAR}`}
                  </div>
                  <div className="v2__cal-list-scroll">
                    {(() => {
                      const items = hoveredDay
                        ? calMonthItems.filter(e => e.day === hoveredDay)
                        : calMonthItems.slice(0, 5);
                      if (!items.length) return <div className="v2__cal-list-empty">No events this {hoveredDay ? 'day' : 'month'}</div>;
                      return items.map((evt, i) => {
                        const card = (
                          <div key={i} className="v2__cal-event" style={{ borderLeftColor: EVENT_COLORS[evt.type] || '#3070e7' }}>
                            <div className="v2__cal-event-date">
                              {MONTH_NAMES[calMonth].slice(0, 3)} {evt.day}
                              <span className={`v2__cal-event-badge v2__cal-event-badge--${evt.required}`}>
                                {evt.required === 'mandatory' ? 'Required' : 'Optional'}
                              </span>
                            </div>
                            <span className="v2__cal-event-type" style={{ color: EVENT_COLORS[evt.type] || '#3070e7' }}>{evt.type}</span>
                            <h4 className="v2__cal-event-title">{evt.title}</h4>
                            {evt.description && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0', lineHeight: 1.4 }}>{evt.description.slice(0, 100)}{evt.description.length > 100 ? '…' : ''}</p>}
                          </div>
                        );
                        return evt.link ? (
                          <a key={i} href={evt.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                            {card}
                          </a>
                        ) : card;
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Courses */}
          <section className="v2__section v2__section--learning" style={{ backgroundImage: 'url(/assets/images/cisco-security-hero.jpg)' }}>
            <div className="v2__wrap">
              <div className="v2__section-top">
                <div>
                  <p className="v2__eyebrow">Enablement</p>
                  <h2 className="v2__heading">Continue Your Learning</h2>
                </div>
              </div>
              <div className="v2__cslider-wrap">
                <button className="v2__cslider-arrow v2__cslider-arrow--left" onClick={() => scroll('left')} aria-label="Previous">&lsaquo;</button>
                <div className="v2__cslider" ref={sliderRef}>
                  {journeyMaps.map((jm: any, i: number) => {
                    const cc = COURSE_CARDS[i % COURSE_CARDS.length];
                    const title = jm.name || jm.title || 'Untitled Journey';
                    const desc = jm.description || '';
                    const steps = jm.item_count ? `${jm.item_count} steps` : null;
                    const duration = jm.estimated_duration_minutes
                      ? jm.estimated_duration_minutes >= 60
                        ? `${Math.round(jm.estimated_duration_minutes / 60)}h`
                        : `${jm.estimated_duration_minutes}m`
                      : null;
                    return (
                      <div key={jm.id} className="v2__ccard" onClick={() => navigate(`/enablement/journey-map/${jm.id}`)} role="button" tabIndex={0}>
                        <span className="v2__ccard-title">{title}</span>
                        <div className="v2__ccard-imgwrap">
                          <div className="v2__ccard-icon" style={{ color: cc.color }}>
                            <HbrIcon asset={cc.icon} />
                          </div>
                          <div className="v2__ccard-img" style={{ background: cc.grad }} />
                        </div>
                        <div className="v2__ccard-body">
                          {desc && <p className="v2__ccard-sum">{String(desc).replace(/<[^>]+>/g, '').slice(0, 150)}</p>}
                          <div className="v2__ccard-footer">
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                              {duration && <span style={{
                                display: 'inline-block',
                                fontSize: '10px',
                                fontWeight: '500',
                                padding: '3px 6px',
                                background: cc.color + '20',
                                color: cc.color,
                                borderRadius: '4px',
                                border: `1px solid ${cc.color}40`,
                              }}>
                                {duration}
                              </span>}
                              {steps && <span style={{
                                display: 'inline-block',
                                fontSize: '10px',
                                fontWeight: '500',
                                padding: '3px 6px',
                                background: cc.color + '20',
                                color: cc.color,
                                borderRadius: '4px',
                                border: `1px solid ${cc.color}40`,
                              }}>
                                {steps}
                              </span>}
                            </div>
                            <span className="v2__ccard-tag">Journey Map</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button className="v2__cslider-arrow v2__cslider-arrow--right" onClick={() => scroll('right')} aria-label="Next">&rsaquo;</button>
              </div>
              <div className="v2__section-cta">
                <HbrButton variant="outline" onClick={() => navigate('/enablement')}>View All Journey Maps</HbrButton>
              </div>
            </div>
          </section>

          {/* Talos Feed */}
          <section className="v2__section v2__section--alt">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Talos Intelligence</p>
              <h2 className="v2__heading">Stay Current</h2>
              <p className="v2__subtext">Stay ahead of emerging threats with the latest from Cisco Talos.</p>

              <div className="v2__feed">
                {rssItems.slice(0, 4).map((item, i) => (
                  <a key={i} className="v2__feed-item" href={postUrl(item.link)} target="_blank" rel="noopener noreferrer">
                    <time>{fmtDate(item.pubDate)}</time>
                    <h4>{item.title}</h4>
                    <p>{item.description}</p>
                  </a>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ===== PROFILE VIEW ===== */}
      {view === 'profile' && (
        <>
          {/* Profile hero */}
          <section className="v2__hero v2__hero--compact">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Your Profile</p>
              <h1 className="v2__hero-heading">{userName || MOCK_USER.name}</h1>
              <p className="v2__hero-sub">{userEmail || MOCK_USER.email}</p>
            </div>
          </section>

          {/* Stats — moved from home */}
          <section className="v2__stats">
            <div className="v2__wrap">
              <div className="v2__stats-grid">
                {oppStats.map(s => (
                  <div key={s.lbl} className="v2__stat">
                    <span className="v2__stat-val">{s.val}</span>
                    <span className="v2__stat-lbl">{s.lbl}</span>
                    <span className="v2__stat-sub">{s.sub}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <OpportunityActivity email={userEmail} />
          <EnablementTable rows={enablementRows} bounds={quarterBoundsForTable} loading={enablementLoading} sample={demoEnablement} />

          {/* Badges + Activity */}
          <section className="v2__section">
            <div className="v2__wrap">
              <div className="v2__profile-row v2__profile-row--single">
                <div className="v2__profile-col v2__profile-col--eval">
                  <SelfEvaluation defaultQuadrant="handson" />
                </div>
              </div>
            </div>
          </section>

          {/* Charts */}
          <section className="v2__section v2__section--alt">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Performance</p>
              <h2 className="v2__heading">Your Progress at a Glance</h2>

              <div className="v2__charts">
                <div className="v2__chart-card" style={{ flex: '1 1 100%' }}>
                  <h3>Performance Pulse — {hasTeamData ? 'You vs Team' : 'Your Numbers'}</h3>
                  <ResponsiveContainer width="100%" height={420}>
                    <RadarChart cx="50%" cy="50%" outerRadius="76%" data={radarData}>
                      <PolarGrid stroke="#d4dae3" />
                      <PolarAngleAxis
                        dataKey="subject"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 500 }}
                      />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar name="You" dataKey="selfNorm" stroke="#049fd9" fill="#049fd9" fillOpacity={0.24} strokeWidth={2.6} />
                      {hasTeamData && (
                        <Radar name="Team avg" dataKey="teamNorm" stroke="#6b7280" fill="#6b7280" fillOpacity={0.12} strokeWidth={2.4} />
                      )}
                      <Tooltip content={<PerfTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
                <div className="v2__chart-card">
                  <h3>Monthly SE Activity</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={engagementData}>
                      <defs>
                        <linearGradient id="v2fillA" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3070e7" stopOpacity={0.12} />
                          <stop offset="100%" stopColor="#3070e7" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e5ea" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2e5ea', borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="demoPitch" name="Demo/Pitch" stroke="#3070e7" fill="url(#v2fillA)" strokeWidth={2} />
                      <Area type="monotone" dataKey="design" name="Customer Design" stroke="#16bdeb" fill="transparent" strokeWidth={1.5} />
                      <Area type="monotone" dataKey="pov" name="POV" stroke="#ff007f" fill="transparent" strokeWidth={1.5} />
                      <Area type="monotone" dataKey="adoption" name="Adoption" stroke="#ff9000" fill="transparent" strokeWidth={1.5} />
                      <Area type="monotone" dataKey="other" name="Other" stroke="#6b7280" fill="transparent" strokeWidth={1} strokeDasharray="4 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          {/* Required Training */}
          <section className="v2__section">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Required Training</p>
              <h2 className="v2__heading">Stay on Track</h2>

              <div className="v2__train-grid">
                {[
                  { heading: 'GSSO Required Training', items: stayOnTrack.gsso, empty: 'All caught up — no incomplete journey maps.' },
                  { heading: 'Required Corporate Training', items: stayOnTrack.corporate, empty: 'All caught up — no outstanding required training.' },
                ].map(section => (
                  <div key={section.heading} className="v2__train-section">
                    <h3 className="v2__train-heading">{section.heading}</h3>
                    {section.items.length ? (
                      <div className="v2__train-list">
                        {section.items.map((t, i) => {
                          const inner = (
                            <>
                              <span className={`v2__train-status v2__train-status--${t.status.toLowerCase().replace(/\s/g, '-')}`}>{t.status}</span>
                              <span className="v2__train-name">{t.label}</span>
                              <span className="v2__train-due">{t.detail}</span>
                            </>
                          );
                          return t.href ? (
                            <a key={i} className="v2__train-item v2__train-item--link" href={t.href} target="_blank" rel="noopener noreferrer">
                              {inner}
                            </a>
                          ) : (
                            <div key={i} className="v2__train-item">{inner}</div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="v2__train-clear">
                        <HbrIcon asset={checkCircle} className="v2__train-clear-icon" />
                        <span>{section.empty}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Recommended Training — Journey Maps */}
          <section className="v2__section v2__section--alt">
            <div className="v2__wrap">
              <p className="v2__eyebrow">Recommended For You</p>
              <h2 className="v2__heading">Training to Help You Level Up</h2>
              <p className="v2__subtext">Structured enablement journeys to build your skills and advance your expertise.</p>

              <div className="v2__reco-grid">
                {journeyMaps.slice(0, 6).map((jm: any, i: number) => {
                  const title = jm.name || jm.title || 'Untitled Journey';
                  const desc = jm.description || 'Structured enablement path for SE skill development.';
                  const cc = COURSE_CARDS[i % COURSE_CARDS.length];
                  const mine = (myJourney.maps || []).find((m) => m.mapId === Number(jm.id));
                  const pct = Math.max(0, Math.min(100, Math.round(mine?.percent ?? 0)));
                  // Estimated time: the map's own estimate, else the sum of its items' estimates (the map's, then yours).
                  const sumMinutes = (list: any[] | undefined) => (list || []).reduce((n, it) => n + (Number(it?.estimated_duration_minutes) || 0), 0);
                  const totalMinutes = [jm.estimated_duration_minutes, sumMinutes(jm.journey_map_items), sumMinutes(mine?.items)].find((n) => Number(n) > 0) as number | undefined;
                  const timeText = !totalMinutes || mine?.done
                    ? null
                    : pct > 0
                      ? `${fmtMinutes(Math.round((totalMinutes * (100 - pct)) / 100))} left`
                      : `${fmtMinutes(totalMinutes)} to complete`;
                  // The list payload has no count, so use the map's own items, else the items loaded for your assignment.
                  const steps = [jm.item_count, jm.journey_map_items?.length, mine?.totalItems].find((n) => Number(n) > 0) as number | undefined;
                  return (
                    <div
                      key={jm.id}
                      className="v2__reco-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/enablement/journey-map/${jm.id}`)}
                      onKeyDown={e => e.key === 'Enter' && navigate(`/enablement/journey-map/${jm.id}`)}
                    >
                      {steps && (
                        <div className="v2__reco-count">
                          <strong style={{ color: cc.color }}>{steps}</strong>
                          <span>{steps === 1 ? 'step' : 'steps'}</span>
                        </div>
                      )}
                      <div className="v2__reco-body">
                        <h4 className="v2__reco-title">{title}</h4>
                        <p className="v2__reco-reason">{String(desc).replace(/<[^>]+>/g, '')}</p>
                        {(timeText || mine) && (
                          <div className="v2__reco-foot">
                            {mine && (
                              <>
                                <span className="v2__reco-track" aria-hidden="true">
                                  <i style={{ width: `${mine.done ? 100 : pct}%`, background: cc.color }} />
                                </span>
                                <span>{mine.done ? 'Completed' : `${pct}% complete`}</span>
                              </>
                            )}
                            {timeText && <span className={`v2__reco-time${mine ? '' : ' v2__reco-time--solo'}`}>{timeText}</span>}
                          </div>
                        )}
                      </div>
                      <span className="v2__reco-go" aria-hidden="true">&rarr;</span>
                    </div>
                  );
                })}
                {journeyMaps.length === 0 && (
                  <p style={{ color: '#6b7280', fontSize: 14 }}>No journey maps available yet.</p>
                )}
              </div>
              {journeyMaps.length > 6 && (
                <div className="v2__section-cta">
                  <HbrButton variant="outline" onClick={() => navigate('/enablement')}>View All Journey Maps</HbrButton>
                </div>
              )}
            </div>
          </section>

        </>
      )}

      {/* Talos Intelligence Banner (fallback when RSS feed unavailable) */}
      {!feedLoaded && (
        <section className="v2__talos-banner" onClick={() => window.open('https://talosintelligence.com/', '_blank', 'noopener,noreferrer')} role="link" tabIndex={0}>
          <div className="v2__wrap v2__talos-inner">
            <img src="/assets/logos/talos2.jpg" alt="Cisco Talos" className="v2__talos-logo" />
            <div className="v2__talos-text">
              <h3 className="v2__talos-title">Cisco Talos Intelligence</h3>
              <p className="v2__talos-desc">Get the latest threat intelligence, vulnerability research, and security advisories from Cisco&apos;s world-class threat research team.</p>
            </div>
            <span className="v2__talos-cta">Explore &rarr;</span>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="v2__footer">
        <div className="v2__wrap">
          <div className="v2__footer-inner">
            <span>&copy; {new Date().getFullYear()} Cisco Systems, Inc.</span>
            <span>Security Hub</span>
          </div>
        </div>
      </footer>

      {/* AI Chatbot */}
      <PortalChatbot />
    </div>
  );
};

export default LandingPageV2;
